import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, useSlots } from 'vue';
import { inputNumberProps, inputNumberEmits } from './inputNumber';
import createComponentId from '../../utils/createComponentId';
import { UPDATE_MODEL_VALUE } from '../../constants';
import { UiIconClose, UiIconDeleteOutline } from '../../icons';
import { Loading } from '../../directives';


export default /*#__PURE__*/_defineComponent({
  __name: 'inputNumber',
  props: inputNumberProps,
  emits: inputNumberEmits,
  setup(__props, { emit: emits }) {

const props = __props



const slots = useSlots();

const inputNumberId = createComponentId('ui-input-number');
const isFocused = ref(false);
const userInput = ref<string | null>(null);
const currentValue = ref<number | null>(null);

const isInteractive = (): boolean => !props.disabled && !props.readonly && !props.loading;

const normalizeRaw = (value: string): string => value.replace(/\s/g, '').replace(',', '.');

const getPrecision = (value: number | null | undefined): number => {
    if (value == null) return 0;
    const str = value.toString();
    const dot = str.indexOf('.');
    return dot === -1 ? 0 : str.length - dot - 1;
};

const numPrecision = computed(() => props.precision ?? 0);

const toPrecision = (num: number, pre?: number): number => {
    const p = pre ?? numPrecision.value;
    if (p === 0) return Math.round(num);
    return Number(Number(num).toFixed(p));
};

const clampValue = (value: number): number => {
    let result = value;
    if (props.min !== undefined && result < props.min) result = props.min;
    if (props.max !== undefined && result > props.max) result = props.max;
    return result;
};

const applyDisplayFormat = (value: string): string => {
    let result = value;
    if (props.thousands) {
        const negative = result.startsWith('-');
        const abs = negative ? result.slice(1) : result;
        const [integer, decimal] = abs.split('.');
        const formatted = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        result = decimal !== undefined ? `${formatted},${decimal}` : formatted;
        if (negative) result = `-${result}`;
    } else if (props.precision !== undefined) {
        result = result.replace('.', ',');
    }
    return result;
};

const displayValue = computed<string>(() => {
    if (userInput.value !== null) return userInput.value;
    if (currentValue.value == null) return '';
    const str = props.precision !== undefined
        ? currentValue.value.toFixed(props.precision)
        : String(currentValue.value);
    return applyDisplayFormat(str);
});

const verifyValue = (value: number | string | null | undefined): number | null => {
    if (value == null || value === '') return null;
    const parsed = Number(value);
    if (isNaN(parsed)) return null;
    const precised = props.precision !== undefined ? toPrecision(parsed, props.precision) : parsed;
    return clampValue(precised);
};

const setCurrentValue = (value: number | string | null | undefined, emitChange = true): void => {
    const oldVal = currentValue.value;
    const newVal = verifyValue(value);
    userInput.value = null;
    if (oldVal === newVal && value != null) return;
    currentValue.value = newVal;
    if (emitChange) emits(UPDATE_MODEL_VALUE, newVal!);
};

const handleInput = (event: Event): void => {
    if (!isInteractive()) return;
    const raw = (event.target as HTMLInputElement).value;
    userInput.value = raw;
    const normalized = normalizeRaw(raw);
    if (normalized === '' || normalized === '-') return;
    const parsed = parseFloat(normalized);
    if (!isNaN(parsed)) emits(UPDATE_MODEL_VALUE, parsed);
};

const handleFocus = (): void => {
    isFocused.value = true;
};

const handleBlur = (event: Event): void => {
    isFocused.value = false;
    if (!isInteractive()) return;
    const raw = normalizeRaw((event.target as HTMLInputElement).value);
    if (raw === '') {
        currentValue.value = null;
        userInput.value = null;
        emits(UPDATE_MODEL_VALUE, '');
        return;
    }
    const parsed = parseFloat(raw);
    if (!isNaN(parsed)) {
        const verified = verifyValue(parsed)!;
        currentValue.value = verified;
        emits(UPDATE_MODEL_VALUE, verified);
    }
    userInput.value = null;
};

const step = (coefficient: 1 | -1): void => {
    if (!isInteractive()) return;
    const base = currentValue.value ?? 0;
    const stepSize = props.precision !== undefined ? Math.pow(10, -props.precision) : 1;
    const factor = Math.pow(10, props.precision ?? 0);
    const newVal = (Math.round(base * factor) + coefficient) / factor;
    const verified = verifyValue(newVal)!;
    currentValue.value = verified;
    userInput.value = null;
    emits(UPDATE_MODEL_VALUE, verified);
};

const ALLOWED_KEYS = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', '-'];

const handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'ArrowUp') {
        event.preventDefault();
        step(1);
        return;
    }
    if (event.key === 'ArrowDown') {
        event.preventDefault();
        step(-1);
        return;
    }
    if (ALLOWED_KEYS.includes(event.key)) return;
    if (event.ctrlKey || event.metaKey) return;
    if (/^\d$/.test(event.key)) return;
    if (props.precision !== undefined && (event.key === ',' || event.key === '.')) return;
    event.preventDefault();
};

const handleChange = (): void => {};

const handleClear = (): void => {
    if (!isInteractive()) return;
    currentValue.value = null;
    userInput.value = null;
    emits(UPDATE_MODEL_VALUE, '');
};

watch(
    () => props.modelValue,
    (value) => {
        if (!isFocused.value) {
            currentValue.value = verifyValue(value);
        }
    },
    { immediate: true }
);

return { __sfc: true,props, emits, slots, inputNumberId, isFocused, userInput, currentValue, isInteractive, normalizeRaw, getPrecision, numPrecision, toPrecision, clampValue, applyDisplayFormat, displayValue, verifyValue, setCurrentValue, handleInput, handleFocus, handleBlur, step, ALLOWED_KEYS, handleKeydown, handleChange, handleClear, UiIconClose, UiIconDeleteOutline }
}

})